/**
 * Generic widget theming.
 *
 * Concrete widgets (booking, gift-card, …) read HTML attributes and forward
 * them through `applyThemeToHost()` which sets CSS custom properties on the
 * Custom Element. Internal styles inside the Shadow DOM consume `--w-*` and
 * widget-specific aliases (e.g. `--bt-*`).
 */

export interface ThemeAttrs {
  accent?: string | null;
  bg?: string | null;
  text?: string | null;
  radius?: string | null;
  font?: string | null;
}

export type ThemeVarName = '--w-accent' | '--w-bg' | '--w-text' | '--w-radius' | '--w-font';

export type ThemeVars = Partial<Record<ThemeVarName, string>>;

const HEX_REGEX = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

function normalizeColor(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  if (HEX_REGEX.test(trimmed)) {
    return trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
  }
  // Accept CSS color keywords, rgb(), hsl(), var() unchanged
  return trimmed;
}

function normalizeRadius(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  if (/^\d+(\.\d+)?$/.test(trimmed)) return `${trimmed}px`;
  return trimmed;
}

export function buildThemeVars(attrs: ThemeAttrs): ThemeVars {
  const vars: ThemeVars = {};
  const accent = normalizeColor(attrs.accent);
  const bg = normalizeColor(attrs.bg);
  const text = normalizeColor(attrs.text);
  const radius = normalizeRadius(attrs.radius);
  const font = attrs.font?.trim();
  if (accent) vars['--w-accent'] = accent;
  if (bg) vars['--w-bg'] = bg;
  if (text) vars['--w-text'] = text;
  if (radius) vars['--w-radius'] = radius;
  if (font) vars['--w-font'] = font;
  return vars;
}

/**
 * Apply theme CSS variables on the host element so the Shadow DOM (and any
 * Light DOM slots projected into it) inherits them.
 */
export function applyThemeToHost(host: HTMLElement, attrs: ThemeAttrs): void {
  const vars = buildThemeVars(attrs);
  const knownVarNames: ThemeVarName[] = ['--w-accent', '--w-bg', '--w-text', '--w-radius', '--w-font'];
  knownVarNames.forEach((varName): void => {
    const value = vars[varName];
    if (value) {
      host.style.setProperty(varName, value);
    } else {
      host.style.removeProperty(varName);
    }
  });
}

/** Names of theme attributes that affect only CSS variables (not React props). */
export const THEME_ATTRIBUTE_NAMES = ['accent', 'bg', 'text', 'radius', 'font'] as const;

export type ThemeAttributeName = (typeof THEME_ATTRIBUTE_NAMES)[number];

/**
 * `:host` des widgets qui embarquent le tunnel (`booking-tunnel.scss` : réservation, carte cadeau).
 *
 * `.bt-root` redéclare ses jetons depuis `--bt-*-override` (même contrat que /book et les sites) : un
 * `--bt-accent` posé sur l'hôte y est écrasé, les attributs de thème restaient donc sans effet (bug 24 de
 * la spec Promotions). Les `--bt-*` restent pour ce qui est rendu hors de `.bt-root`. Chaque surcharge
 * renvoie à son `--w-*` SANS repli : attribut absent → variable invalide → `.bt-root` garde ses défauts.
 */
export const TUNNEL_HOST_STYLES = `
  :host {
    display: block;
    font-family: var(--w-font, system-ui, -apple-system, sans-serif);
    color: var(--w-text, #1a1a1a);
    --bt-accent: var(--w-accent, #000);
    --bt-bg: var(--w-bg, #fff);
    --bt-text: var(--w-text, #1a1a1a);
    --bt-radius: var(--w-radius, 8px);
    --bt-font: var(--w-font, inherit);
    --bt-accent-override: var(--w-accent);
    --bt-bg-override: var(--w-bg);
    --bt-text-override: var(--w-text);
    --bt-radius-override: var(--w-radius);
    --bt-font-override: var(--w-font);
  }
`;
